import React, { useContext, useMemo, useRef, MouseEventHandler, useEffect, useState, TouchEventHandler, FunctionComponent } from "react";
import { useObservableValue } from "@iventis/utilities";
import { useThrottledResizeObserver } from "../../calendar/components/hooks";
import { IGridContext } from "../state/interactive-grid-context";
import { DragHelper } from "../utilities/drag-helper";
import { extractClientXClientY } from "../utilities/helpers";

/**
 * Interactive Grid Clip Zone
 * The component which provides a window into the interactive area
 * Hides the overflow of all the contents and manages panning events
 */
export const IGridInteractiveClipZone: FunctionComponent<{ children?: React.ReactNode }> = ({ children }) => {
    const ctx = useContext(IGridContext);

    const pannableRange = useObservableValue(ctx.pannableRange);

    const dragHelper = useMemo(() => new DragHelper([0, 0]), []);

    const clipZoneElement = useRef<HTMLDivElement>();

    const resize = useThrottledResizeObserver(400, clipZoneElement);

    const [savedMoveListener, setMoveListener] = useState<(e: MouseEvent) => void>();

    const onMouseDown = (e: MouseEvent | TouchEvent) => {
        const { clientX, clientY } = extractClientXClientY(e);

        dragHelper.setInitialCursor([clientX, clientY]);
        dragHelper.draggingBegan(pannableRange);

        let notifiedPanning = false;

        const mouseMoveListener = (e: MouseEvent | TouchEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (!notifiedPanning) {
                // Only notify of panning once
                notifiedPanning = true;
                ctx.setPanning(true);
            }

            const { clientX, clientY } = extractClientXClientY(e);

            const diff = dragHelper.getDifference([clientX, clientY]);
            ctx.updateTranslation(...diff);
        };

        setMoveListener(() => mouseMoveListener);

        document.addEventListener("mousemove", mouseMoveListener);
        document.addEventListener("touchmove", mouseMoveListener);

        const mouseUpListener = () => {
            setTimeout(() => {
                /*
                    Click events occur directly after mouse up, therefore we have to only notify of
                    panning completion after the click event would have completed.
                    It would be impossible for any other user interraction to occur in the time between the two
                    stack execution contexts since the mouse events are guaranteed to fire one after the other
                */
                notifiedPanning = false;
                ctx.setPanning(false);
            });

            dragHelper.draggingEnded(ctx.translation.value);
            document.removeEventListener("mousemove", mouseMoveListener);
            document.removeEventListener("touchmove", mouseMoveListener);
            document.removeEventListener("mouseup", mouseUpListener);
            document.removeEventListener("touchend", mouseUpListener);
        };

        document.addEventListener("mouseup", mouseUpListener);
        document.addEventListener("touchend", mouseUpListener);
    };

    useEffect(() => {
        ctx.updateClipZoneSize(resize.width, resize.height);

        return () => {
            if (savedMoveListener) {
                document.removeEventListener("mousemove", savedMoveListener);
                document.removeEventListener("touchmove", savedMoveListener);
            }
        };
    }, [clipZoneElement.current, resize.width, resize.height]);

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            ref={clipZoneElement}
            id="igrid-clipzone"
            data-cy="interactive-grid"
            onMouseDown={(onMouseDown as unknown) as MouseEventHandler}
            onTouchStart={(onMouseDown as unknown) as TouchEventHandler}
            style={{ flexGrow: 1, overflow: "hidden", position: "relative", touchAction: "none" }}
        >
            {children}
        </div>
    );
};
